import { Component, inject } from '@angular/core';
import { TopicStore } from '../../../stores/topic/topic.state';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { Topic } from '../../../models/topic';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DividerModule } from 'primeng/divider';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { IconDropdownComponent } from '../../atoms/icon-dropdown/icon-dropdown.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { InputSwitchModule } from 'primeng/inputswitch';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { EditorModule } from 'primeng/editor';
import { TabMenuModule } from 'primeng/tabmenu';
import { MenuItem } from 'primeng/api';
import { PickListModule } from 'primeng/picklist';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkDropdownComponent } from '../../atoms/bookmark-dropdown/bookmark-dropdown.component';
import { HeaderComponent } from '../../atoms/header/header.component';

@Component({
  selector: 'app-topic-form',
  standalone: true,
  imports: [
    ButtonModule,
    ColorPickerModule,
    DividerModule,
    FormFieldWrapperComponent,
    IconDropdownComponent,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    ReactiveFormsModule,
    InputSwitchModule,
    WorkspaceDropdownComponent,
    FormFooterComponent,
    EditorModule,
    TabMenuModule,
    PickListModule,
    BookmarkDropdownComponent,
    HeaderComponent,
  ],
  templateUrl: './topic-form.component.html',
  styleUrl: './topic-form.component.scss',
})
export class TopicFormComponent extends BaseFormComponent<Topic> {
  readonly store = inject(TopicStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  selectedTab = 'general';

  bookmarkCategories: BookmarkCategory[] = [];
  selectedCategories: BookmarkCategory[] = [];

  tabItems: MenuItem[] = [
    {
      label: 'Grundeinstellungen',
      icon: 'fas fa-book-open',
      id: 'general',
      routerLink: ['../general'],
    },
    {
      label: 'Themenbereiche',
      icon: 'fas fa-layer-group',
      id: 'lesezeichen',
      routerLink: ['../lesezeichen'],
    },
  ];
  activeTabItem: MenuItem = this.tabItems[0];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
  }

  /**
   * Initializes a form with default or pre-existing values for editing or creation.
   * This includes setting the active tab based on route parameters, configuring
   * default values for form fields, and marking the form as initialized.
   *
   * @return {void} This method does not return any value.
   */
  initializeForm(): void {
    this.selectedTab =
      this.routingService.getParameterFromRoute('tab') || 'general';
    if (this.selectedTab !== 'general') this.activeTabItem = this.tabItems[1];

    this.setCategorySelection();

    this.form = this.fb.group({
      title: [this.itemToEdit?.title || '', Validators.required],
      description: [this.itemToEdit?.description || ''],
      icon: [this.itemToEdit?.icon || '', Validators.required],
      defaultBookmark: [this.itemToEdit?.defaultBookmark || null],
      bookmarkCategories: [this.itemToEdit?.bookmarkCategories || []],
      published: [this.itemToEdit?.published || false],
      workspaces: [this.itemToEdit?.workspaces || []],
    });
    this.formInitialized = true;
  }

  /**
   * Handles the submission logic when the form or action is triggered.
   * Executes the defined submit process.
   *
   * @return {void} This method does not return a value.
   */
  onSubmit(): void {
    this.submit();
  }

  /**
   * Handles the logic for setting the category selection for bookmarks.
   * Updates the `selectedCategories` based on the categories included in
   * the `itemToEdit` bookmark and populates the `bookmarkCategories` list
   * with the remaining categories.
   *
   * @return {void} Does not return any value.
   */
  setCategorySelection(): void {
    const bookmarkCategories = this.bookmarkCategoryStore.asArray();
    this.bookmarkCategories = [];
    if (this.itemToEdit && this.itemToEdit.bookmarkCategories) {
      this.selectedCategories = bookmarkCategories.filter(category => {
        if (this.itemToEdit?.bookmarkCategories.includes(category._id)) {
          return true;
        } else {
          this.bookmarkCategories.push(category);
          return false;
        }
      });
    } else {
      this.selectedCategories = [];
      this.bookmarkCategories = bookmarkCategories;
    }
  }

  /**
   * Updates the form value by patching the 'bookmarkCategories' field with the IDs of the selected categories.
   *
   * The method retrieves the selected categories stored in the `selectedCategories` property, extracts their `_id` values,
   * and applies these values to the 'bookmarkCategories' field in the form state using the `patchValue` method.
   *
   * @*/
  updateFormValue(): void {
    this.form.patchValue({
      bookmarkCategories: this.selectedCategories.map(category => category._id),
    });
  }

  /**
   * Handles the tab change event by updating the active tab item and selected tab.
   *
   * @param {MenuItem} $event - The menu item representing the new active tab.
   * @return {void} This method does not return a value.
   */
  onTabChange($event: MenuItem): void {
    this.activeTabItem = $event;
    this.selectedTab = this.activeTabItem.id || 'general';
  }
}
